import { IS_ANDROID_APP } from '../utils/platform';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useSiteConfig } from '../contexts/SiteConfigContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  Play, 
  Crown, 
  GraduationCap, 
  Trophy, 
  CheckCircle,
  ArrowRight,
  Brain,
  Target,
  Sparkles,
  Star,
  Lock,
  LogIn,
  UserPlus
} from 'lucide-react';
import Navbar from '../components/Navbar';

const features = [
  {
    icon: Brain,
    title: '17 Difficulty Levels',
    description: 'From Naked Singles to Forcing Chains - master every technique',
  },
  {
    icon: GraduationCap,
    title: 'Interactive Tutorials',
    description: 'Learn each solving technique with step-by-step guidance',
  },
  {
    icon: Trophy,
    title: 'Daily Challenges',
    description: 'Compete on leaderboards and track your progress',
  },
  {
    icon: Target,
    title: 'Pattern Highlighting',
    description: 'Visual hints show you exactly which technique to apply',
  },
];

const techniques = [
  'Naked Single', 'Hidden Single', 'Naked Pair', 'Hidden Pair',
  'Pointing Pair', 'Box-Line Reduction', 'X-Wing', 'Swordfish',
  'XY-Wing', 'XYZ-Wing', 'Skyscraper', '2-String Kite',
  'W-Wing', 'Simple Coloring', 'AIC', 'ALS', 'Forcing Chain'
];

// Mini 9×9 grid renderer for the spotlight card. Pure presentation —
// no interactivity. The grid string is one of the curated NYT L4 puzzles
// from the bank (IMG_1497, 23 givens, Hidden Pair).
const SPOTLIGHT_GRID =
  '000541000006070008090008000560020010002000400000007000080305009000010006035000000';

function MiniGrid({ grid }) {
  return (
    <div
      className="grid grid-cols-9 gap-px bg-[var(--border-medium)] p-px rounded-md"
      style={{ width: 260, height: 260 }}
      aria-label="Curated NYT sudoku puzzle preview"
      data-testid="spotlight-grid"
    >
      {grid.split('').map((ch, i) => {
        const r = Math.floor(i / 9);
        const c = i % 9;
        // Slightly darker background every other 3×3 box so the structure
        // reads at a glance (mirrors NYT's printed visual style).
        const boxKey = (Math.floor(r / 3) + Math.floor(c / 3)) % 2;
        return (
          <div
            key={i}
            className="flex items-center justify-center font-mono text-[12px] text-[var(--text-primary)]"
            style={{
              backgroundColor:
                boxKey === 0
                  ? 'var(--background-elevated)'
                  : 'var(--background-surface)',
            }}
          >
            {ch === '0' ? '' : ch}
          </div>
        );
      })}
    </div>
  );
}

function CuratedSpotlight({ onUpgrade, premiumGating }) {
  // When premium gating is disabled (adoption mode), this spotlight
  // morphs from a "Premium-only — upgrade" CTA into a "Play the curated
  // NYT puzzle now (free during launch)" CTA. Same hand-picked content,
  // different framing depending on whether we're driving signups or
  // upgrades.
  return (
    <section
      className="py-16 px-4"
      data-testid="curated-spotlight-section"
    >
      <div className="max-w-6xl mx-auto">
        <div
          className="rounded-2xl border border-[#FFD60A]/30 bg-gradient-to-br from-[#1a1a0a] via-[var(--background-surface)] to-[var(--background-main)] p-8 sm:p-10 flex flex-col md:flex-row items-center gap-8"
          data-testid="curated-spotlight-card"
        >
          <MiniGrid grid={SPOTLIGHT_GRID} />

          <div className="flex-1 text-center md:text-left">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold bg-[#FFD60A]/15 text-[#FFD60A] border border-[#FFD60A]/40 mb-3">
              <Star className="w-3 h-3" />
              <span>NYT · Hand-Curated</span>
            </div>

            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[var(--text-primary)] mb-3">
              {premiumGating ? (
                <>This week&apos;s curated puzzle is&nbsp;<span className="text-[#FFD60A]">Premium-only</span>.</>
              ) : (
                <>Play this week&apos;s curated puzzle —&nbsp;<span className="text-[#FFD60A]">free during launch</span>.</>
              )}
            </h2>

            <p className="text-sm text-[var(--text-secondary)] mb-5 max-w-xl">
              Hand-picked from The&nbsp;New&nbsp;York&nbsp;Times. 23 clues. Hidden
              Pair territory. Pure deductive logic — no guessing.
              {premiumGating
                ? ' Free play covers Levels 1–3; this puzzle and the rest of our curated library start at Level 4.'
                : ' Every level is open during our beta — jump straight in, no sign-up required.'}
            </p>

            <ul className="text-xs text-[var(--text-disabled)] space-y-1 mb-6 max-w-md mx-auto md:mx-0">
              <li className="flex items-center gap-2 justify-center md:justify-start">
                <CheckCircle className="w-3.5 h-3.5 text-[#34C759]" /> Pure-logic
                solver + progressive hints
              </li>
              <li className="flex items-center gap-2 justify-center md:justify-start">
                <CheckCircle className="w-3.5 h-3.5 text-[#34C759]" /> NYT-grade
                curated set (growing every week)
              </li>
              <li className="flex items-center gap-2 justify-center md:justify-start">
                <CheckCircle className="w-3.5 h-3.5 text-[#34C759]" /> Step-by-step
                walkthrough of every deduction
              </li>
            </ul>

            <div className="flex flex-col sm:flex-row gap-3">
              {premiumGating ? (
                <Button
                  onClick={onUpgrade}
                  className="bg-[#FFD60A] hover:bg-[#FFE45A] text-black font-semibold px-6 py-5"
                  data-testid="spotlight-upgrade-btn"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Unlock the curated library — $6.99/mo
                </Button>
              ) : (
                <Link
                  to="/play"
                  className="inline-flex items-center justify-center bg-[#FFD60A] hover:bg-[#FFE45A] text-black font-semibold px-6 py-5 rounded transition-colors"
                  data-testid="spotlight-play-btn"
                >
                  <Star className="w-4 h-4 mr-2" />
                  Play now — free
                </Link>
              )}
              <Link
                to="/play"
                className="inline-flex items-center justify-center px-6 py-3 rounded text-sm font-medium border border-[var(--border-medium)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--hover-bg)] transition-colors"
                data-testid="spotlight-free-btn"
              >
                {premiumGating ? 'Or try Levels 1–3 free' : 'Browse all 17 levels'}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function LandingPage() {
  const { user, loading } = useAuth();
  const { premiumGatingEnabled } = useSiteConfig();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-main)' }}>
      <Navbar />

      {/* Sign-in call-out card — prominent boxed CTA directly under the header
          for unauthenticated visitors. Two large buttons + a subtle explainer.
          Hidden entirely for signed-in users so we don't nag them. */}
      {!user && (
        <section className="px-4 pt-6" data-testid="login-callout-section">
          <div
            className="max-w-3xl mx-auto rounded-2xl border-2 border-[#8B5A3C]/45 bg-[var(--background-surface)] shadow-lg p-5 sm:p-6"
            data-testid="login-callout-card"
            style={{ boxShadow: '0 6px 24px rgba(59, 36, 22, 0.10)' }}
          >
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
              <div className="hidden sm:flex w-12 h-12 rounded-full bg-[#8B5A3C]/12 items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-[#8B5A3C]" />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h2 className="font-heading text-lg sm:text-xl font-bold text-[var(--text-primary)] mb-1">
                  Sign in to save your streak
                </h2>
                <p className="text-sm text-[var(--text-secondary)]">
                  Track progress across devices, join daily leaderboards, and unlock 17 difficulty levels.
                </p>
              </div>
              <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
                <Button
                  onClick={() => navigate('/login')}
                  variant="outline"
                  className="flex-1 sm:flex-none border-[#8B5A3C]/60 text-[#8B5A3C] hover:bg-[#8B5A3C]/10 font-semibold"
                  data-testid="landing-signin-btn"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign In
                </Button>
                <Button
                  onClick={() => navigate('/register')}
                  className="flex-1 sm:flex-none bg-[#8B5A3C] hover:bg-[#A46944] text-white font-semibold"
                  data-testid="landing-signup-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Sign Up Free
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Hero Section */}
      <section className="buddhabrot-hero relative py-20 px-4">
        <div 
          className="absolute inset-0 z-0 pointer-events-none"
          style={{ 
            backgroundImage: `url(${process.env.PUBLIC_URL}/buddhabrot.jpg)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.6,
            mixBlendMode: 'screen',
          }}
        />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#8B5A3C]/10 border border-[#8B5A3C]/20 mb-8">
              <Sparkles className="w-4 h-4 text-[#8B5A3C]" />
              <span className="text-sm text-[#8B5A3C] font-medium">Master the Art of Logic</span>
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-[var(--text-primary)] tracking-tighter mb-6">
              The Ultimate
              <br />
              <span className="text-[#8B5A3C]">Sudoku Academy</span>
            </h1>
            
            <p className="text-lg text-[var(--text-secondary)] mb-8 max-w-2xl mx-auto">
              17 difficulty levels based on real solving techniques. Learn, practice, and compete 
              with the most comprehensive Sudoku platform ever built.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/play')}
                className="bg-[#8B5A3C] hover:bg-[#A46944] text-white font-semibold px-8 py-6 text-lg"
                data-testid="hero-cta"
              >
                <Play className="w-5 h-5 mr-2" />
                Start Playing Free
              </Button>
              {!IS_ANDROID_APP && <Button
                onClick={() => navigate('/subscription')}
                variant="outline"
                className="border-[var(--border-medium)] text-[var(--text-primary)] hover:bg-[var(--hover-bg)] px-8 py-6 text-lg"
                data-testid="hero-pricing"
              >
                View Premium
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>}
            </div>
          </div>
        </div>
      </section>

      {/* Curated Spotlight — premium-only NYT puzzle as social proof + conversion driver */}
      <CuratedSpotlight onUpgrade={() => navigate('/subscription')} premiumGating={premiumGatingEnabled && !IS_ANDROID_APP} />

      {/* Features Grid */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[var(--text-primary)] mb-4">
              More Than Just Puzzles
            </h2>
            <p className="text-[var(--text-secondary)] max-w-2xl mx-auto">
              A complete learning platform designed to take you from beginner to expert.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature) => (
              <Card 
                key={feature.title} 
                className="bg-[var(--background-surface)] border-[var(--border-subtle)] hover:border-[var(--border-medium)] transition-colors"
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded bg-[#8B5A3C]/10 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-[#8B5A3C]" />
                  </div>
                  <h3 className="font-heading font-semibold text-[var(--text-primary)] mb-2">{feature.title}</h3>
                  <p className="text-sm text-[var(--text-secondary)]">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Techniques Section */}
      <section className="py-20 px-4" style={{ backgroundColor: 'color-mix(in srgb, var(--background-surface) 50%, transparent)' }}>
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[var(--text-primary)] mb-4">
                17 Techniques to Master
              </h2>
              <p className="text-[var(--text-secondary)] mb-6">
                Each difficulty level is named after the most advanced technique required to solve it.
                Progress through all 17 levels and become a true Sudoku master.
              </p>
              <div className="flex flex-wrap gap-2">
                {techniques.slice(0, 8).map((tech) => (
                  <span 
                    key={tech}
                    className="px-3 py-1 rounded bg-[var(--background-elevated)] border border-[var(--border-subtle)] text-sm text-[var(--text-secondary)]"
                  >
                    {tech}
                  </span>
                ))}
                <span className="px-3 py-1 rounded bg-[#8B5A3C]/20 border border-[#8B5A3C]/30 text-sm text-[#8B5A3C]">
                  +9 more...
                </span>
              </div>
              <Button
                onClick={() => navigate('/tutorials')}
                variant="outline"
                className="mt-6 border-[var(--border-medium)] text-[var(--text-primary)] hover:bg-[var(--hover-bg)]"
                data-testid="view-tutorials"
              >
                <GraduationCap className="w-4 h-4 mr-2" />
                Explore Tutorials
              </Button>
            </div>
            <div 
              className="aspect-video rounded-lg bg-cover bg-center opacity-80"
              style={{
                backgroundImage: 'url(https://images.unsplash.com/photo-1771011726574-60ab6028724a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGRhcmslMjBnZW9tZXRyaWMlMjBncmlkfGVufDB8fHx8MTc3NTEwODc2OHww&ixlib=rb-4.1.0&q=85)',
              }}
            />
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      {!IS_ANDROID_APP && <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[var(--text-primary)] mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-[var(--text-secondary)]">
              Start free, upgrade when you&apos;re ready for more.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Free */}
            <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)]">
              <CardHeader>
                <CardTitle className="text-[var(--text-primary)] font-heading">Free</CardTitle>
                <CardDescription className="text-[var(--text-secondary)]">No account needed</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-[var(--text-primary)] mb-4">$0</div>
                <ul className="space-y-2 text-sm text-[var(--text-secondary)]">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#34C759]" />
                    Levels 1-3 (no sign-up)
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#34C759]" />
                    Step-by-step hints
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#34C759]" />
                    Solve walkthroughs
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Premium */}
            <Card className="bg-[var(--background-surface)] border-[#8B5A3C] relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="px-3 py-1 rounded-full bg-[#8B5A3C] text-white text-xs font-semibold">
                  FULL ACCESS
                </span>
              </div>
              <CardHeader>
                <CardTitle className="text-[var(--text-primary)] font-heading flex items-center gap-2">
                  <Crown className="w-5 h-5 text-[#8B5A3C]" />
                  Premium
                </CardTitle>
                <CardDescription className="text-[var(--text-secondary)]">Everything unlocked</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-[var(--text-primary)] mb-1">$4.99<span className="text-sm font-normal text-[var(--text-secondary)]">/mo</span></div>
                <div className="text-sm text-[#34C759] font-medium mb-4">or $49/yr · or $89 one-time (Lifetime)</div>
                <ul className="space-y-2 text-sm text-[var(--text-secondary)]">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#34C759]" />
                    All 17 difficulty levels
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#34C759]" />
                    Unlimited puzzles
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#34C759]" />
                    Interactive tutorials
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#34C759]" />
                    Ad-free experience
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-8">
            <Button
              onClick={() => navigate('/subscription')}
              className="bg-[#8B5A3C] hover:bg-[#A46944] text-white"
              data-testid="pricing-cta"
            >
              Compare Plans
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>}

      {/* Footer */}
      <footer className="border-t border-[var(--border-subtle)] py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            <div>
              <h3 className="font-heading text-xl font-bold text-[var(--text-primary)] mb-2">SudokuAcademy.org</h3>
              <p className="text-[var(--text-disabled)] text-sm mb-3">
                The ultimate platform for mastering Sudoku techniques.
              </p>
              <nav className="flex flex-wrap gap-x-5 gap-y-2 text-sm" aria-label="Footer">
                <Link to="/science" className="text-[#8B5A3C] hover:underline" data-testid="footer-science-link">
                  Science &amp; History
                </Link>
                <Link to="/privacy" className="text-[#8B5A3C] hover:underline" data-testid="footer-privacy-link">
                  Privacy Policy
                </Link>
                <Link to="/support" className="text-[#8B5A3C] hover:underline" data-testid="footer-support-link">
                  Support
                </Link>
              </nav>
            </div>
            <NewsletterForm />
          </div>
          <p className="mt-10 pt-6 border-t border-[var(--border-subtle)] text-xs text-[var(--text-secondary)]" data-testid="footer-copyright">
            © {new Date().getFullYear()} SudokuAcademy.org. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

function NewsletterForm() {
  const [email, setEmail] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`,
        { email: trimmed }
      );
      toast.success(data.message || 'Subscribed!');
      setEmail('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || 'Please enter a valid email address.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3" data-testid="newsletter-form">
      <div>
        <h4 className="font-heading text-sm font-semibold text-[var(--text-primary)] mb-1">
          Newsletter
        </h4>
        <p className="text-[var(--text-disabled)] text-sm">
          Monthly tips, new techniques, and reviewer updates.
        </p>
      </div>
      <div className="flex gap-2 flex-wrap">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          required
          className="flex-1 min-w-[200px] px-3 py-2 rounded border border-[var(--border-subtle)] bg-[var(--background-surface)] text-[var(--text-primary)] focus:outline-none focus:border-[#8B5A3C] text-sm"
          data-testid="newsletter-email-input"
        />
        <Button
          type="submit"
          disabled={submitting || !email.trim()}
          className="bg-[#8B5A3C] hover:bg-[#A46944] text-white text-sm"
          data-testid="newsletter-submit-btn"
        >
          {submitting ? 'Subscribing…' : 'Subscribe'}
        </Button>
      </div>
    </form>
  );
}
